import cn from 'classnames';
import { capitalize } from 'lodash';
import { observer } from 'mobx-react';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Table, theme } from 'antd';

import { DefaultBehaviour, EngineStepsEnum } from '@/store/Engine/constants';
import { useStores } from '@/hooks/use-stores';
import TableHeaderCell from '@/comp/pages/EnginePage/Engine/Common/TableHeaderCell/TableHeaderCell';
import EditIcon from '@/comp/common/icons/EditIcon';
import EpModal from '@/comp/EPModal';
import { ValidationMessagePopoverGlobalStyle } from '@/comp/pages/EnginePage/Engine/Common/styled';
import { Radio } from '@/comp/pages/EnginePage/Common/styled';
import RulesGeneratorModal from '@/comp/pages/EnginePage/Engine/Common/RulesGeneratorModal/RulesGeneratorModal';
import { EPButton, EPSelect, EPSelectOption } from '@/components';

import { Card, SensorsPriorityModalContentContainer } from './styled';
import Range from '../Common/Range/Range';
import { sortSetpointsByElementIndex } from '@/store/Engine/utils';
import { RangeType } from '../Common/Range/Range.utils';

const { useToken } = theme;

const SensorsPriorityModalContent = observer(
  ({
    type,
    sensorsPriority,
    sensorsPriorityColumns,
    onOkClick,
    onCancelClick,
    setHasTriedToSubmit,
    hasTriedToSubmit,
  }) => {
    const { t } = useTranslation();
    const { engineStore, stationStore } = useStores();
    const [isRulesGeneratorModalOpen, setIsRulesGeneratorModalOpen] = useState(false);

    const onRadioSelect = e => {
      if (e?.target?.value === DefaultBehaviour.ExistingRule)
        sensorsPriority?.setField(
          'ruleParentKey',
          engineStore.mainRangesConfiguration?.pressureLevels?.ranges?.[0]?.key,
        );
      else sensorsPriority?.setField('ruleParentKey', undefined);
      if (e?.target?.value !== DefaultBehaviour.CustomRule && sensorsPriority?.rules?.length)
        sensorsPriority?.setField('rules', undefined);
      sensorsPriority?.setField('defaultBehaviour', e?.target?.value);
    };

    const onSelectChange = value => sensorsPriority?.setField('ruleParentKey', value);

    const onRulesGeneratorClick = () => {
      setHasTriedToSubmit(false);
      setIsRulesGeneratorModalOpen(true);
    };

    const onRulesGeneratorSaveClick = () => {
      sensorsPriority?.setField(
        'rules',
        sensorsPriority?.rules?.map(rule => ({ ...rule, oldValues: { ...rule } })),
      );
      setIsRulesGeneratorModalOpen(false);
    };

    const onOk = e => {
      engineStore.clearValidationMessages();
      engineStore.validate(
        EngineStepsEnum.SensorsConfiguration,
        `${type}SensorsPriority`,
        undefined,
        stationStore.currentStation.devices,
      );
      if (
        (sensorsPriority?.defaultBehaviour === DefaultBehaviour.CustomRule
          ? sensorsPriority?.rules?.[0]?.name
          : true) &&
        !engineStore.validationMessages?.length
      )
        onOkClick(e);
    };

    const onCancel = e => {
      sensorsPriority?.cancelChanges();
      onCancelClick(e);
    };

    return (
      <SensorsPriorityModalContentContainer>
        <span className="sensors-fallback">{t(`${type}SensorsFallback`)}</span>
        {sensorsPriority?.sensorsPriority?.length ? (
          <Table
            columns={sensorsPriorityColumns}
            dataSource={sensorsPriority?.sensorsPriority}
            pagination={false}
            className={cn('table', 'sensors-priority-table', 'sensors-priority-modal-table')}
          />
        ) : null}
        <div className="default-behaviour">
          <div className="default-behaviour-label">{t('defaultBehaviour')}</div>
          <Radio.Group
            value={sensorsPriority?.defaultBehaviour}
            className="default-behaviour-radio-group"
            onChange={onRadioSelect}
          >
            <Radio value={DefaultBehaviour.ExistingRule}>{t('chooseRule')}</Radio>
            {sensorsPriority?.defaultBehaviour === DefaultBehaviour.ExistingRule && (
              <EPSelect value={sensorsPriority?.ruleParentKey} onChange={onSelectChange} size="small">
                {engineStore.setpointsConfiguration?.getUniqueRangeSetpoints()?.map(setpoint => (
                  <EPSelectOption key={setpoint?.parentRangeKey} value={setpoint?.parentRangeKey}>
                    {setpoint?.name}
                  </EPSelectOption>
                ))}
              </EPSelect>
            )}
            <Radio value={DefaultBehaviour.CustomRule}>{t('customRule')}</Radio>
            {sensorsPriority?.defaultBehaviour === DefaultBehaviour.CustomRule && (
              <EPButton size="medium" type="primary" className="rule-generator-button" onClick={onRulesGeneratorClick}>
                <span className="button-label">{sensorsPriority?.rules?.[0]?.name || t('ruleGenerator')}</span>
              </EPButton>
            )}
            <Radio value={DefaultBehaviour.TurnOffEngine} disabled={true} className={'disabled'}>
              {t('turnOffEngine')}
            </Radio>
          </Radio.Group>
        </div>
        <div className="bottom-buttons">
          <EPButton type="primary" onClick={onCancel}>
            <span className="button-label">{t('cancel')}</span>
          </EPButton>
          <EPButton type="primary" onClick={onOk}>
            <span className="button-label">{t('ok')}</span>
          </EPButton>
        </div>
        <EpModal
          open={isRulesGeneratorModalOpen}
          width="1032"
          height="600"
          centered
          styles={{ body: { padding: 0 } }}
          closable={false}
          maskClosable={false}
        >
          <RulesGeneratorModal
            rulesObject={sensorsPriority}
            onOk={onRulesGeneratorSaveClick}
            hasTriedToSubmit={hasTriedToSubmit}
            setHasTriedToSubmit={setHasTriedToSubmit}
            type={type}
          />
        </EpModal>
        <ValidationMessagePopoverGlobalStyle />
      </SensorsPriorityModalContentContainer>
    );
  },
);

const SensorsPriority = observer(({ type, setHasTriedToSubmit, hasTriedToSubmit }) => {
  const { t } = useTranslation();
  const {
    engineStore: {
      mainRangesConfiguration: { pressureLevels, unit },
      sensorsConfiguration,
      setpointsConfiguration,
      isReadOnly,
    },
    devicesStore,
  } = useStores();
  const [shouldDisplayModal, setShouldDisplayModal] = useState(false);
  const { token } = useToken();

  const sensorsPriority =
    type === RangeType.Flow ? sensorsConfiguration?.flowSensorsPriority : sensorsConfiguration?.pressureSensorsPriority;

  const sensorsPriorityColumns = [
    {
      dataIndex: 'priority',
      key: 'priority',
      title: ' ',
      width: '20%',
      render: priority => <TableHeaderCell titleKey={priority === 0 ? t('main') : t('fallback', { priority })} />,
    },
    {
      dataIndex: 'aggregation',
      key: 'aggregation',
      title: <TableHeaderCell titleKey="logic" />,
      width: '15%',
      render: aggregation => <span className="table-cell">{capitalize(aggregation)}</span>,
    },
    {
      dataIndex: 'availability',
      key: 'availability',
      title: <TableHeaderCell titleKey="online" />,
      width: '15%',
      render: availability => <span className="table-cell">{t(availability)}</span>,
    },
    {
      dataIndex: 'sensors',
      key: 'sensors',
      title: <TableHeaderCell titleKey="sensors" />,
      width: '*',
      render: sensors => (
        <span className="table-cell">
          {sensors?.map(sensor => (
            <span className="chip-item" key={sensor?.name} style={{ marginRight: token.marginXS, marginBottom: token.marginXXS }}>
              {sensor?.name}
            </span>
          ))}
        </span>
      ),
    },
  ];

  const onEditSensorsPriorityClick = () => setShouldDisplayModal(true);

  const onEditSensorsPriorityModalClose = () => {
    if (sensorsPriority?.hasChanges()) sensorsPriority?.cancelChanges();
    setShouldDisplayModal(false);
  };

  const onOkClick = () => setShouldDisplayModal(false);

  const onCancelClick = () => setShouldDisplayModal(false);

  return (
    <>
      <span className="card-item-title">
        {t(`${type}SensorsPriority`)}
        {!isReadOnly && <EditIcon className="icon" onClick={onEditSensorsPriorityClick} />}
      </span>
      {sensorsPriority?.sensorsPriority?.length ? (
        <Table
          columns={sensorsPriorityColumns}
          dataSource={sensorsPriority?.sensorsPriority}
          pagination={false}
          className={cn('table', 'sensors-priority-table')}
        />
      ) : null}
      {(sensorsPriority?.defaultBehaviour === DefaultBehaviour.ExistingRule && sensorsPriority?.ruleParentKey) ||
      (sensorsPriority?.defaultBehaviour === DefaultBehaviour.CustomRule && sensorsPriority?.rules?.length) ? (
        <Range
          fromFallbackOverview
          unit={unit}
          type={RangeType.Pressure}
          range={
            sensorsPriority?.defaultBehaviour === DefaultBehaviour.ExistingRule
              ? pressureLevels.getRangeByKey(sensorsPriority?.ruleParentKey)
              : { name: sensorsPriority?.rules?.[0]?.name }
          }
          setpoints={
            sensorsPriority?.defaultBehaviour === DefaultBehaviour.CustomRule
              ? sortSetpointsByElementIndex(sensorsPriority?.rules, devicesStore?.sortedCompressors)
              : setpointsConfiguration.getSetpointsByPressureRange(sensorsPriority?.ruleParentKey)
          }
        />
      ) : null}
      <EpModal
        open={!!shouldDisplayModal}
        onCancel={onEditSensorsPriorityModalClose}
        centered
        width="fit-content"
        height="fit-content"
        closable={false}
        maskClosable={false}
        styles={{ body: { backgroundColor: token.colorBgContainer, padding: token.paddingXL } }}
      >
        <SensorsPriorityModalContent
          type={type}
          sensorsPriority={sensorsPriority}
          sensorsPriorityColumns={sensorsPriorityColumns}
          onOkClick={onOkClick}
          onCancelClick={onCancelClick}
          setHasTriedToSubmit={setHasTriedToSubmit}
          hasTriedToSubmit={hasTriedToSubmit}
        />
      </EpModal>
    </>
  );
});

const SensorsLogic = observer(({ setHasTriedToSubmit, hasTriedToSubmit }) => {
  const { t } = useTranslation();

  return (
    <Card>
      <span className="card-title">{t('sensorsLogic')}</span>
      <div className="card-body">
        <div className="card-item-container">
          <div className="card-item">
            <SensorsPriority
              type="flow"
              setHasTriedToSubmit={setHasTriedToSubmit}
              hasTriedToSubmit={hasTriedToSubmit}
            />
          </div>
        </div>
        <div className="card-item-container">
          <div className="card-item">
            <SensorsPriority
              type="pressure"
              setHasTriedToSubmit={setHasTriedToSubmit}
              hasTriedToSubmit={hasTriedToSubmit}
            />
          </div>
        </div>
      </div>
    </Card>
  );
});

export default SensorsLogic;
